import React, { useState, useRef, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import * as XLSX from "xlsx";
import { getReportApi, insertIndividualPrintApi, getLabelPrintApi } from "../Api/api";

// Function to load PRN template from file
const loadTemplate = async (templateName) => {
  try {
    const response = await fetch(`/prn_templates/${templateName}.txt`);
    if (!response.ok) {
      throw new Error(`Failed to load template: ${response.status}`);
    }
    return await response.text();
  } catch (error) {
    console.error("Error loading template:", error);
    return null;
  }
};

// Build preview by repeating only the first-row template sections batch-by-batch.
const generateDynamicTemplate = (baseTemplate, labelCount, labelsPerRow = 4, setupTxt = "") => {
  const normalizedTemplate = (baseTemplate || "").replace(/\r\n/g, "\n");
  const safeLabelsPerRow = Math.max(1, parseInt(labelsPerRow, 10) || 4);

  const p1Index = normalizedTemplate.indexOf("P1");
  const beforeP1 = p1Index !== -1 ? normalizedTemplate.substring(0, p1Index) : normalizedTemplate;

  const labelStart =
    beforeP1.indexOf("; --- Label 1 ---") !== -1
      ? beforeP1.indexOf("; --- Label 1 ---")
      : beforeP1.search(/(?:^|\n)b\d+,\d+,Q,m2,s3,eL,iA,o6,/m);

  if (labelStart === -1) {
    return normalizedTemplate;
  }

  const fileSetupBlock = beforeP1.substring(0, labelStart).replace(/\s*$/, "");
  const templateBody = beforeP1.substring(labelStart).trim();
  const dbSetupBlock = (setupTxt || "").replace(/\r\n/g, "\n").trim();
  const effectiveSetupBlock = dbSetupBlock || fileSetupBlock || "N";

  const slotSections = [];

  // Preferred path: marker-based sections (; --- Label 1 ---).
  if (templateBody.includes("; --- Label 1 ---")) {
    for (let slot = 1; slot <= safeLabelsPerRow; slot++) {
      const startMarker = `; --- Label ${slot} ---`;
      const endMarker = `; --- Label ${slot + 1} ---`;
      const start = templateBody.indexOf(startMarker);

      if (start === -1) {
        continue;
      }

      const end = templateBody.indexOf(endMarker, start + startMarker.length);
      const section = end === -1 ? templateBody.substring(start).trim() : templateBody.substring(start, end).trim();
      slotSections.push({ slot, section });
    }
  } else {
    // Fallback path: unmarked templates (split by QR command lines).
    const lines = templateBody.split("\n");
    let currentSection = [];

    for (const line of lines) {
      const trimmed = line.trim();
      const isQrStart = /^b\d+,\d+,Q,m2,s3,eL,iA,o6,/.test(trimmed);

      if (isQrStart) {
        if (currentSection.length > 0) {
          const section = currentSection.join("\n").trim();
          if (section) {
            slotSections.push({ slot: slotSections.length + 1, section });
          }
        }
        currentSection = [line];
      } else if (currentSection.length > 0) {
        currentSection.push(line);
      }
    }

    if (currentSection.length > 0) {
      const section = currentSection.join("\n").trim();
      if (section) {
        slotSections.push({ slot: slotSections.length + 1, section });
      }
    }
  }

  if (slotSections.length === 0) {
    return normalizedTemplate;
  }

  const totalBatches = Math.ceil(labelCount / safeLabelsPerRow);
  const output = [];

  for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
    output.push(effectiveSetupBlock);
    output.push("");

    for (const { slot, section } of slotSections) {
      const globalIndex = batchIndex * safeLabelsPerRow + slot;
      if (globalIndex > labelCount) {
        continue;
      }

      const remappedSection = section.replace(/\{\{([A-Z0-9_]+)_(\d+)\}\}/g, (full, key, idx) => {
        return parseInt(idx, 10) === slot ? `{{${key}_${globalIndex}}}` : full;
      });

      output.push(remappedSection);
      output.push("");
    }

    // End-of-batch page break only between batches.
    if (batchIndex < totalBatches - 1) {
      output.push("P1");
      output.push("N");
    }
  }

  output.push("P1");
  output.push("N");

  return output.join("\n").replace(/\n{3,}/g, "\n\n").trim() + "\n";
};

// Function to bind data to template variables
const bindTemplateData = (templateContent, data) => {
  if (!templateContent || !data) return "";
  
  let boundContent = templateContent;
  
  console.log("Binding data keys:", Object.keys(data));
  console.log("Template content snippet:", templateContent.substring(0, 100));
  
  // Generate QRDATA fields if they don't exist in the response
  for (let i = 1; i <= 12; i++) {
    const qrKey = `QRDATA_${i}`;
    if (!data[qrKey]) {
      // Generate QR data from other fields.
      const partNo = data[`PART_NO_${i}`] || "";
      const prodNo = data[`PROD_NO_${i}`] || "";
      const vcode = data[`VCODE_${i}`] || "";
      const addlTxt = data[`ADDL_TXT_${i}`] || "";
      data[qrKey] = `${partNo}$${prodNo}$${vcode}$${addlTxt}`.trim();
    } else {
      // Normalize API values to '$' delimiter for QR payload.
      data[qrKey] = String(data[qrKey]).replace(/\|/g, "$");
    }
  }
  
  // Replace all {{variable}} placeholders with data values
  Object.keys(data).forEach(key => {
    const placeholder = `{{${key}}}`;
    const value = data[key] !== null && data[key] !== undefined ? data[key] : "";
    console.log(`Trying to replace ${placeholder} with ${value}`);
    
    // Check if the placeholder exists in the template
    if (templateContent.includes(placeholder)) {
      console.log(`Found placeholder ${placeholder}`);
      boundContent = boundContent.replace(new RegExp(placeholder, 'g'), value);
    }
  });
  
  return boundContent;
};


function Reports() {
  const today = new Date();
  const defaultDate = today.toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState(defaultDate);
  const [toDate, setToDate] = useState(defaultDate);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [labelsGenerated, setLabelsGenerated] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [boundTemplateData, setBoundTemplateData] = useState("");
  const [popupPosition, setPopupPosition] = useState({ x: 120, y: 80 });
  const popupDragRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });

  // Popup drag functionality
  useEffect(() => {
    if (showPopup) {
      const initialX = Math.max(20, Math.floor((window.innerWidth - 800) / 2));
      setPopupPosition({ x: initialX, y: 60 });
    }
  }, [showPopup]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!popupDragRef.current.dragging) return;

      const panelWidth = 800;
      const panelHeight = 640;
      const x = e.clientX - popupDragRef.current.offsetX;
      const y = e.clientY - popupDragRef.current.offsetY;

      const clampedX = Math.min(Math.max(20, x), Math.max(20, window.innerWidth - panelWidth - 20));
      const clampedY = Math.min(Math.max(20, y), Math.max(20, window.innerHeight - panelHeight - 20));
      setPopupPosition({ x: clampedX, y: clampedY });
    };

    const handleMouseUp = () => {
      popupDragRef.current.dragging = false;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const loadReport = async () => {
    if (!fromDate || !toDate) {
      alert("Please select both From and To dates");
      return;
    }

    setLoading(true);
    try {
      // Get company short code from localStorage
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const comShortCode = userData.ComShortCode || "";
      
      const result = await getReportApi(
        `${fromDate} 00:00:00`,
        `${toDate} 23:59:59`,
        comShortCode
      );

      if (result.status === "success") {
        setData(result.data);
        // Check if summary is at top level or inside array
        if (result.TotalLabels || result.TotalRecords) {
          setSummary({
            TotalLabels: result.TotalLabels,
            TotalRecords: result.TotalRecords
          });
        } else if (result.summary?.[0]) {
          setSummary(result.summary[0]);
        } else {
          setSummary(null);
        }
      } else {
        setData([]);
        setSummary(null);
      }
    } catch (error) {
      console.error("Error loading report:", error);
      setData([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };


  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const filteredData = data.filter((item) => {
  const search = searchTerm.toLowerCase();

  return (
    item.PartNo?.toLowerCase().includes(search) ||
    item.PartDesc?.toLowerCase().includes(search) ||
    item.TemplateName?.toLowerCase().includes(search) ||
    item.ComShortCode?.toLowerCase().includes(search) ||
    item.UserTxt1?.toLowerCase().includes(search) ||
    item.UserTxt2?.toLowerCase().includes(search) ||
    item.AddlTxt1?.toLowerCase().includes(search) ||
    item.AddlTxt2?.toLowerCase().includes(search) ||
    item.ProductionNo?.toString().includes(search) ||
    item.Vcode?.toString().includes(search)
  );
});

  // Calculate summary stats
const totalLabels = filteredData.reduce(
  (sum, item) => sum + (item.LabelQty || 0),
  0
);

const totalRecords = filteredData.length;

// const exportToCSV = () => {
//   if (!filteredData || filteredData.length === 0) {
//     alert("No data to export");
//     return;
//   }

//   const headers = [
//     "Vcode","PartNo","ProductionNo","PrdDate","BatchCode","HeatCode",
//     "MaterialCode","Drawinglevel","PartDesc","UserTxt1","UserTxt2",
//     "AddlTxt1","AddlTxt2","TemplateName","ComShortCode","LabelQty"
//   ];

//   const rows = filteredData.map((item) =>
//     headers.map((key) => {
//       let value = item[key];

//       if (key === "PrdDate" && value) {
//         value = new Date(value).toLocaleDateString("en-GB");
//       }

//       return value ?? "";
//     })
//   );

//   const csv =
//     [headers, ...rows]
//       .map(row =>
//         row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")
//       )
//       .join("\r\n");

//   // ✅ Add BOM for Excel UTF-8 support
//   const blob = new Blob(["\uFEFF" + csv], {
//     type: "text/csv;charset=utf-8;"
//   });

//   const url = URL.createObjectURL(blob);

//   const link = document.createElement("a");
//   link.href = url;
//   link.download = `Production_Report_${fromDate}_to_${toDate}.csv`;

//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
// };

const exportToCSV = () => {
  if (!filteredData || filteredData.length === 0) {
    alert("No data to export");
    return;
  }

  const headers = [
    "Vcode","PartNo","ProductionNo","PrdDate","BatchCode","HeatCode",
    "MaterialCode","Drawinglevel","PartDesc","UserTxt1","UserTxt2",
    "AddlTxt1","AddlTxt2","TemplateName","ComShortCode","LabelQty"
  ];

  const rows = filteredData.map((item) =>
    headers.map((key) => {
      let value = item[key];

      if (key === "PrdDate" && value) {
        value = new Date(value).toLocaleDateString("en-GB");
      }

      return value ?? "";
    })
  );

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Production Report");

  XLSX.writeFile(
    workbook,
    `Production_Report_${fromDate}_to_${toDate}.xlsx`
  );
};

const generateLabels = async () => {
  if (selectedRows.length === 0) {
    alert("Please select at least one Production No");
    return;
  }

  try {
    // Get company short code from localStorage
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const comShortCode = userData.ComShortCode || "";
    
    // Call the API with selected PrintcntIDs
    const response = await insertIndividualPrintApi({
      ComShortCode: comShortCode,
      ItemsJSON: JSON.stringify(selectedRows)
    });

    if (response.status === "success") {
      alert(response.message);
      setLabelsGenerated(true);
    } else {
      alert(response.message || "Failed to generate labels");
    }
  } catch (error) {
    console.error("Error generating labels:", error);
    alert("Error generating labels. Please try again.");
  }
};

const showLabels = async () => {
  try {
    // Get company short code from localStorage
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const comShortCode = userData.ComShortCode || "";
    
    // Get LogicProcedure from first record in report data
    const procName = data.length > 0 ? data[0].LogicProcedure : null;
    
    // Call the API to get label print data
    const response = await getLabelPrintApi(comShortCode, procName);

    if (response.status === "success" && response.data && response.data.length > 0) {
      // Transform the API response data into template-friendly format
      const data = {};
      
      response.data.forEach((label, index) => {
        const idx = index + 1; // 1-based index for template
        
        // Map all fields from the API response to template variables with index suffix
        Object.keys(label).forEach(key => {
          data[`${key}_${idx}`] = label[key] || "";
        });
      });
      
      console.log("Transformed label print data:", data);
      
      // Determine how many labels are in the response
      const labelCount = response.data.length;
      console.log("Number of labels in response:", labelCount);
      
      // Get the template name from the first label (assuming all labels use the same template)
      const templateName = response.data[0].TemplateName || "Label_4x4";
      
      // Load the base template
      let templateContent = await loadTemplate(templateName);
      
      if (templateContent) {
        // Generate dynamic template based on label count
        // For now, we'll use default values since we don't have template list in Reports page
        const labelsPerRow = 4;
        const setupTxt = "";
        let dynamicTemplate = generateDynamicTemplate(templateContent, labelCount, labelsPerRow, setupTxt);
        const boundContent = bindTemplateData(dynamicTemplate, data);
        setBoundTemplateData(boundContent);
        setShowPopup(true);
      }
    } else {
      alert(response.message || "Failed to retrieve labels");
    }
  } catch (error) {
    console.error("Get label print error:", error);
    alert("Error retrieving labels. Please try again.");
  }
};

const toggleRowSelection = (printcntId) => {
  setSelectedRows((prev) =>
    prev.includes(printcntId)
      ? prev.filter((id) => id !== printcntId)
      : [...prev, printcntId]
  );
};

  const styles = {
    container: {
      minHeight: "100vh",
      background: "#e5e7eb",
      padding: "20px",
      marginLeft: "60px"
    },
    header: {
      color: "#1e3a5f",
      marginBottom: "30px",
      fontSize: "28px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "12px"
    },
    headerIcon: {
      width: "40px",
      height: "40px",
      background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a8a 100%)",
      borderRadius: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "20px",
      color: "#fff"
    },
    filterCard: {
      background: "#fff",
      borderRadius: "12px",
      padding: "24px",
      marginBottom: "24px",
      border: "1px solid #d1d5db",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
    },
    filterRow: {
      display: "flex",
      gap: "16px",
      alignItems: "flex-end",
      flexWrap: "wrap"
    },
    inputGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "8px"
    },
    label: {
      color: "#374151",
      fontSize: "13px",
      fontWeight: "600",
      marginBottom: "6px",
      display: "block"
    },
    input: {
      padding: "12px 16px",
      borderRadius: "8px",
      border: "1px solid #d1d5db",
      background: "#fff",
      color: "#1f2937",
      fontSize: "14px",
      outline: "none",
      minWidth: "180px",
      transition: "all 0.3s ease",
      boxSizing: "border-box"
    },
    button: {
      padding: "12px 28px",
      borderRadius: "8px",
      border: "none",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "600",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      gap: "8px"
    },
    primaryButton: {
      background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a8a 100%)",
      color: "#fff"
    },
    secondaryButton: {
      background: "#f3f4f6",
      color: "#1f2937",
      border: "1px solid #d1d5db"
    },
    tableCard: {
      background: "#fff",
      borderRadius: "12px",
      overflow: "hidden",
      border: "1px solid #d1d5db",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      overflowX: "auto"
    },

    wrapTextTd: {
  padding: "6px 10px",
  fontSize: "12px",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "normal",        // 🔥 allow wrapping
  wordBreak: "break-word",     // 🔥 break long words
  maxWidth: "180px",           // control width
  lineHeight: "1.4"
},


    table: {
      width: "100%",
      borderCollapse: "collapse"
    },
   th: {
  background: "#1e3a5f",
  color: "#fff",
  padding: "8px 10px",        // reduced
  textAlign: "left",
  fontWeight: "600",
  fontSize: "12px",           // smaller font
  borderBottom: "1px solid #d1d5db",
  whiteSpace: "nowrap"
},

td: {
  padding: "6px 10px",        // reduced
  color: "#374151",
  fontSize: "12px",           // smaller font
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap"
},

    trHover: {
      transition: "all 0.2s ease"
    },
    statusBadge: {
      padding: "2px 6px",
      borderRadius: "20px",
      fontSize: "11px",
      fontWeight: "600",
      textTransform: "uppercase"
    },
    badgeSuccess: {
      background: "rgba(46, 204, 113, 0.2)",
      color: "#2ecc71"
    },
    badgeInfo: {
      background: "rgba(52, 152, 219, 0.2)",
      color: "#3498db"
    },
    summaryCards: {
      display: "flex",
      gap: "20px",
      marginBottom: "24px",
      flexWrap: "wrap"
    },
    summaryCard: {
      background: "#fff",
      borderRadius: "12px",
      padding: "20px 28px",
      border: "1px solid #d1d5db",
      display: "flex",
      alignItems: "center",
      gap: "16px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
    },
    summaryIcon: {
      width: "48px",
      height: "48px",
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "24px"
    },
    summaryValue: {
      color: "#1e3a5f",
      fontSize: "28px",
      fontWeight: "700"
    },
    summaryLabel: {
      color: "#6b7280",
      fontSize: "13px"
    },
    emptyState: {
      textAlign: "center",
      padding: "60px 20px",
      color: "#6b7280"
    },
    emptyIcon: {
      fontSize: "48px",
      marginBottom: "16px"
    },
    loadingOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000
    },
    loadingSpinner: {
      width: "50px",
      height: "50px",
      border: "4px solid rgba(255, 255, 255, 0.3)",
      borderTopColor: "#667eea",
      borderRadius: "50%",
      animation: "spin 1s linear infinite"
    }
  };

  return (
    <div style={{ background: "#e5e7eb", minHeight: "100vh" }}>
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          tr:hover {
            background: #f3f4f6 !important;
          }
        `}
      </style>
      
      <Sidebar currentPage="reports" />
      
      <Header title="REPORTS" />
      
      <div style={{ marginLeft: "60px", padding: "20px" }}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerIcon}>📊</div>
          <span>Production Reports</span>
        </div>

        {/* Filter Card */}
        <div style={styles.filterCard}>
         <div style={styles.filterRow}>

  {/* From Date */}
  <div style={styles.inputGroup}>
    <label style={styles.label}>From Date</label>
    <input
      type="date"
      value={fromDate}
      onChange={(e) => setFromDate(e.target.value)}
      style={styles.input}
    />
  </div>

  {/* To Date */}
  <div style={styles.inputGroup}>
    <label style={styles.label}>To Date</label>
    <input
      type="date"
      value={toDate}
      onChange={(e) => setToDate(e.target.value)}
      style={styles.input}
    />
  </div>

  {/* Search Box */}
  <div style={styles.inputGroup}>
    <label style={styles.label}>Search</label>
    <input
      type="text"
      placeholder="Search anything..."
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      style={{
        ...styles.input,
        minWidth: "250px"
      }}
    />
  </div>

  {/* Load Button */}
  <button
    onClick={loadReport}
    style={{ ...styles.button, ...styles.primaryButton }}
  >
    🔍 Load
  </button>



  <button
    onClick={generateLabels}
    style={{
      ...styles.button,
      background: "#2563eb",
      color: "#fff"
    }}
  >
    ⚙ Generate
  </button>
  
  <button
    onClick={showLabels}
    style={{
      ...styles.button,
      background: "#16a34a",
      color: "#fff",
      ...(labelsGenerated ? {} : { background: "#9ca3af", cursor: "not-allowed" })
    }}
    disabled={!labelsGenerated}
  >
    👁️ Show Labels
  </button>
    {/* Export Button */}
  <button
    onClick={exportToCSV}
    style={{
      ...styles.button,
      background: "#16a34a",
      color: "#fff"
    }}
  >
    ⬇ Export
  </button>

  <button
    onClick={() => {
      setSelectedRows([]);
      setLabelsGenerated(false);
    }}
    style={{
      ...styles.button,
      background: "#ef4444",
      color: "#fff"
    }}
  >
    ❌ Clear
  </button>
</div>
        </div>

        {/* Summary Cards */}
        {data.length > 0 && (
          <div style={styles.summaryCards}>
            <div style={styles.summaryCard}>
              <div style={{...styles.summaryIcon, background: "rgba(102, 126, 234, 0.2)"}}>
                📑
              </div>
              <div>
                <div style={styles.summaryValue}>{summary?.TotalLabels || totalLabels}</div>
                
                <div style={styles.summaryLabel}>Total Records</div>
              </div>
            </div>
            <div style={styles.summaryCard}>
              <div style={{...styles.summaryIcon, background: "rgba(46, 204, 113, 0.2)"}}>
                🏷️
              </div>
              <div>
               <div style={styles.summaryValue}>{summary?.TotalRecords || totalRecords}</div>
                <div style={styles.summaryLabel}>Total Labels</div>
              </div>
            </div>
          </div>
        )}

        {/* Table Card */}
        <div style={styles.tableCard}>
          {data.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📄</div>
              <p style={{fontSize: "16px", marginBottom: "8px"}}>No records found</p>
              <p style={{fontSize: "14px"}}>Select date range and click "Load Report" or "Load Sample Data"</p>
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Vendor code</th>
                  <th style={styles.th}>Part No</th>
                  <th style={styles.th}>Production No</th>
                  <th style={styles.th}>Select</th>
                  <th style={styles.th}>Mfg Date</th>
                  <th style={styles.th}>BatchCode</th>
                  <th style={styles.th}>HeatCode</th>
                  <th style={styles.th}>MaterialCode</th>
                  <th style={styles.th}>Drawinglevel</th>
                  <th style={styles.th}>Description</th>
                  <th style={styles.th}>User Text 1</th>
                  <th style={styles.th}>User Text 2</th>
                  <th style={styles.th}>Add Text 1</th>
                  <th style={styles.th}>Add Text 2</th>
                  <th style={styles.th}>Template</th>
                   <th style={styles.th}>ComShortCode</th>
                  <th style={styles.th}>Label Qty</th>
                 

                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, index) => (
                   <tr 
                     key={index} 
                     style={styles.trHover}
                   >
                     <td style={styles.td}>{item.Vcode}</td>
                     <td style={styles.td}><strong>{item.PartNo}</strong></td>
                     <td style={styles.td}><strong>{item.ProductionNo}</strong></td>
                     <td style={styles.td}>
                       <input
                         type="checkbox"
                         checked={selectedRows.includes(item.PrintcntID)}
                         disabled={!item.PrintcntID}   
                         onChange={() => toggleRowSelection(item.PrintcntID)}
                       />
                     </td>
                     {/* <td style={styles.td}>{formatDate(item.PrdDate)}</td> */}
                     <td style={styles.td}>
                      {new Date(item.PrdDate).toLocaleDateString('en-GB')}
                    </td>
                     <td style={styles.td}>{item.BatchCode}</td>
                     <td style={styles.td}>{item.HeatCode}</td>
                     <td style={styles.td}>{item.MaterialCode}</td>
                     <td style={styles.td}>{item.Drawinglevel}</td>
                     <td style={styles.td}>{item.PartDesc}</td>
                     <td style={styles.wrapTextTd}>{item.UserTxt1}</td>
                     <td style={styles.wrapTextTd}>{item.UserTxt2}</td>
                     <td style={styles.wrapTextTd}>{item.AddlTxt1}</td>
                     <td style={styles.wrapTextTd}>{item.AddlTxt2}</td>
                     <td style={styles.td}>{item.TemplateName}</td>
                      <td style={styles.td}>{item.ComShortCode}</td>
                     <td style={styles.td}>
                       <span style={{
                         ...styles.statusBadge,
                         ...styles.badgeSuccess
                       }}>
                         {item.LabelQty}
                       </span>
                     </td>
                   </tr>
                 ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Label Preview Popup */}
      {showPopup && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1000
        }}>
          <div style={{
            position: "fixed",
            left: `${popupPosition.x}px`,
            top: `${popupPosition.y}px`,
            width: "min(800px, calc(100vw - 40px))",
            backgroundColor: "#fff",
            borderRadius: "8px",
            padding: "24px",
            maxHeight: "60vh",
            overflow: "auto",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)"
          }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
              cursor: "move",
              userSelect: "none"
            }}
            onMouseDown={(e) => {
              popupDragRef.current.dragging = true;
              popupDragRef.current.offsetX = e.clientX - popupPosition.x;
              popupDragRef.current.offsetY = e.clientY - popupPosition.y;
              e.preventDefault();
            }}
          >
            <h2 style={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#1f2937",
              margin: 0
            }}>
              Label Preview
            </h2>
            
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(boundTemplateData);
                    alert("Label content copied!");
                  } catch (error) {
                    alert("Copy failed");
                  }
                }}
                style={{
                  background: "#10b981",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px 14px",
                  fontSize: "13px",
                  fontWeight: 500,
                  cursor: "pointer"
                }}
              >
                Copy
              </button>
              
              <button
                onClick={() => setShowPopup(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "22px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  color: "#6b7280"
                }}
              >
                ×
              </button>
            </div>
          </div>
          
          <div style={{
            backgroundColor: "#f3f4f6",
            padding: "16px",
            borderRadius: "6px",
            fontFamily: "monospace",
            fontSize: "12px",
            whiteSpace: "pre-wrap",
            overflowX: "auto"
          }}>
            {boundTemplateData}
          </div>
          </div>
        </div>
      )}
      
      {/* Loading Overlay */}
      {loading && (
        <div style={styles.loadingOverlay}>
          <div style={styles.loadingSpinner}></div>
        </div>
      )}
    </div>
  );
}

export default Reports;
